import { isNil } from "lodash";
import React, { ReactNode } from "react";

import { FancyPrice, Price, RoundingStrategy } from "@zapier/currency";
import { Colors, Typography } from "@zapier/design-system";

import { Currency, Plan } from "@/types";
import { css } from "@emotion/react";

type PlanPriceProps = {
  /** The type of currency to display. */
  currency: Currency;
  /** An array of identical plans with different variants */
  planVariants: Plan[];
  /** Shows billing details below price if true. Hides billing details otherwise. */
  showDetails?: boolean;
  /** As default, yearly plan is shown, this flag forces to display the monthly variant plan price */
  shouldShowMonthly?: boolean;
  /** Text to override the default details text shown when showDetails is true - except for Free plan */
  detailsOverrideText?: string;
};

const priceWrapperCss = css`
  display: flex;
  align-items: center;
`;

export function FancyPlanPrice({
  currency,
  children,
  isAnnualPlan,
}: {
  currency: Currency;
  children: number;
  isAnnualPlan: boolean;
}) {
  const fancyPriceProps: React.ComponentProps<typeof FancyPrice> = {
    currency,
    children,
    isSmall: true,
  };

  if (currency.zeroDecimal && isAnnualPlan) {
    fancyPriceProps.roundingStrategy = RoundingStrategy.up;
  }

  return <FancyPrice {...fancyPriceProps} />;
}

const detailStyle = css`
  color: ${Colors.neutral800};
  ${Typography.smallPrint1}
`;

const Details: React.FC = ({ children }) => <p css={detailStyle}>{children}</p>;

type GetDetailsParams = {
  currency: Currency;
  monthlyPlan?: Plan;
  yearlyPlan?: Plan | null;
  detailsOverrideText?: string;
};

const getDetails = ({
  currency,
  monthlyPlan,
  yearlyPlan,
  detailsOverrideText,
}: GetDetailsParams): ReactNode | undefined | null => {
  // User's on a free plan
  if (monthlyPlan && monthlyPlan.amount === 0) {
    return <Details>free forever</Details>;
  }

  if (detailsOverrideText) {
    return <Details>{detailsOverrideText}</Details>;
  }

  let line1 = null;
  let line2 = null;
  // User's on a non-legacy plan that's not free plan
  if (monthlyPlan && yearlyPlan && monthlyPlan.amount !== 0) {
    line1 = "per month, billed annually";
    line2 = (
      <span>
        {" "}
        or <Price currency={currency}>{monthlyPlan.amount}</Price>{" "}
        month-to-month
      </span>
    );
  }

  // User's on a legacy plan (only their currently selected interval is available)
  else {
    line1 = `per month, billed ${monthlyPlan ? "monthly" : "annually"}`;
  }

  return (
    <div>
      <Details>
        {line1}
        {line2}
      </Details>
    </div>
  );
};

/**
 * A component that shows a plan's price and billing details.
 */
export const PlanPrice = ({
  currency,
  showDetails = true,
  shouldShowMonthly = false,
  detailsOverrideText,
  planVariants,
}: PlanPriceProps) => {
  const monthly = planVariants.find((plan) => plan.interval === "month");
  const yearly = planVariants.find((plan) => plan.interval === "year");
  const yearlyPlan = shouldShowMonthly ? null : yearly;
  const details = showDetails
    ? getDetails({
        currency,
        monthlyPlan: monthly,
        yearlyPlan,
        detailsOverrideText,
      })
    : null;

  const payment =
    yearly && !shouldShowMonthly ? yearly.amount / 12 : monthly?.amount ?? 0;

  return (
    <div>
      {!isNil(payment) && (
        <div css={priceWrapperCss}>
          <FancyPlanPrice
            currency={currency}
            isAnnualPlan={Boolean(yearly && !shouldShowMonthly)}
          >
            {payment}
          </FancyPlanPrice>
        </div>
      )}
      {details}
    </div>
  );
};
